import React, { useState, useEffect, useRef } from 'react';
import {
  generateTxnRef, computeKeyTest, formatUSD, formatDate, todayISO, swiftDate,
  KTT_EMAIL, KTT_CODE, SECRET_KEY_CONSTANT, COMPANY,
  toUSD, getFXRate, formatFXAmount
} from '../utils/keyGenerator';

const PRIORITIES = [
  { id: 'NORMAL', label: 'Normal', score: '0-29', desc: 'Standard interbank payment' },
  { id: 'HIGH', label: 'High', score: '30-59', desc: 'Time-sensitive obligation' },
  { id: 'URGENT', label: 'Urgent', score: '60-89', desc: 'CLS / margin call / critical' },
  { id: 'CRITICAL', label: 'Critical', score: '89', desc: 'Must-settle — CCP, sovereign' },
];

const RTGS_SYSTEMS = [
  { code: 'BOS-RTGS', name: 'BOS RTGS (Platform)', currency: 'USD' },
  { code: 'FEDWIRE', name: 'Fedwire Funds Service (Fed)', currency: 'USD' },
  { code: 'CHIPS', name: 'CHIPS (TCH)', currency: 'USD' },
  { code: 'CHAPS', name: 'CHAPS (Bank of England)', currency: 'GBP' },
  { code: 'TARGET2', name: 'TARGET2 / T2 (ECB)', currency: 'EUR' },
  { code: 'TIPS', name: 'TIPS Instant (ECB)', currency: 'EUR' },
  { code: 'SIC', name: 'SIC (SIX/SNB)', currency: 'CHF' },
  { code: 'BOJNET', name: 'BOJ-NET (Bank of Japan)', currency: 'JPY' },
  { code: 'RITS', name: 'RITS (RBA)', currency: 'AUD' },
  { code: 'LYNX', name: 'LYNX (Bank of Canada)', currency: 'CAD' },
  { code: 'BAHTNET', name: 'BAHTNET (BOT)', currency: 'THB' },
  { code: 'CUSTOM-RTGS', name: 'Custom RTGS Connector', currency: 'ANY', custom: true, status: 'pending' },
];

// Steps: 1=RTGS CONFIG, 2=PAYMENT, 3=REVIEW, 4=SETTLEMENT RESULT
export default function SendViaRTGS({ wallet, addTransaction, setSlip, accounts, preSelectedAccount, onAccountUsed }) {
  const [step, setStep] = useState(1);
  const [sourceAccountIban, setSourceAccountIban] = useState(preSelectedAccount?.iban || '');
  const [rtgsSystem, setRtgsSystem] = useState('BOS-RTGS');
  const [priority, setPriority] = useState('NORMAL');
  const [form, setForm] = useState({
    beneficiary: '', accountNo: '', receiverBank: '', receiverBIC: '',
    receiverKTTCode: '', currency: 'USD', amount: '', narrative: '',
    valueDate: todayISO(), senderBIC: '',
  });
  const [alert, setAlert] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [balanceChecks, setBalanceChecks] = useState([]);
  const [isoMessage, setIsoMessage] = useState('');
  const [customForm, setCustomForm] = useState({
    institutionName: '', bic: '', licenseNumber: '', licenseType: '', regulator: '',
    country: '', endpoint: '', currency: 'USD', contactEmail: '', notes: '',
  });
  const [customSubmitted, setCustomSubmitted] = useState(false);

  const txnRef = useRef(generateTxnRef('RTGS'));
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Auto-select source account when navigating from Account page
  useEffect(() => {
    if (preSelectedAccount?.iban) {
      setSourceAccountIban(preSelectedAccount.iban);
      if (onAccountUsed) onAccountUsed();
    }
  }, [preSelectedAccount, onAccountUsed]);

  const keyTest = form.amount && form.valueDate
    ? computeKeyTest(form.amount, form.valueDate) : '-----';

  // Sync wallet balance to RTGS backend on mount
  useEffect(() => {
    fetch('/api/rtgs/sync-balance', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ balance: wallet.balance }),
    }).catch(() => {});
  }, [wallet.balance]);

  // Step 1 → 2
  const handleConfigDone = () => {
    if (rtgsSystem === 'CUSTOM-RTGS') {
      setAlert({ type: 'error', msg: 'Custom RTGS Connector is pending integration. Submit your institution details to apply.' });
      return;
    }
    setAlert(null);
    setStep(2);
  };

  const setCustom = k => e => setCustomForm(f => ({ ...f, [k]: e.target.value }));
  const handleCustomSubmit = () => {
    if (!customForm.institutionName || !customForm.bic || !customForm.licenseNumber) {
      setAlert({ type: 'error', msg: 'Institution Name, BIC/SWIFT, and License Number are required.' });
      return;
    }
    setCustomSubmitted(true);
    setAlert(null);
  };

  // Step 2 → 3
  const handleGoToReview = () => {
    if (!form.beneficiary) { setAlert({ type: 'error', msg: 'Beneficiary is required.' }); return; }
    if (!form.accountNo) { setAlert({ type: 'error', msg: 'Account number is required.' }); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setAlert({ type: 'error', msg: 'Enter a valid amount.' }); return; }
    const usdEquiv = toUSD(parseFloat(form.amount), form.currency);
    if (usdEquiv > wallet.balance) { setAlert({ type: 'error', msg: `Insufficient balance. ${formatFXAmount(parseFloat(form.amount), form.currency)} = ${formatUSD(usdEquiv)} USD. Available: ${formatUSD(wallet.balance)}` }); return; }
    setAlert(null);
    setStep(3);
  };

  // Step 3 → 4: Submit to RTGS engine
  const handleSubmitSettlement = async () => {
    setSending(true);
    const amt = parseFloat(form.amount);

    try {
      const resp = await fetch('/api/rtgs/settle', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form, amount: amt, priority, rtgsSystem,
          receiverKTTCode: form.receiverKTTCode,
          sourceAccountIban: sourceAccountIban || undefined,
        }),
      });
      const data = await resp.json();

      setResult(data);
      setBalanceChecks(data.balanceChecks || []);
      setIsoMessage(data.isoMessage || '');

      if (data.success) {
        // Convert to USD for wallet debit
        const debitUSD = toUSD(amt, form.currency);
        const fxRate = getFXRate(form.currency);
        addTransaction({
          type: 'RTGS', mt: `RTGS-${priority}`,
          debitAmount: debitUSD,
          description: `RTGS ${rtgsSystem} ${priority} — ${form.narrative || form.beneficiary}${form.currency !== 'USD' ? ` (${formatFXAmount(amt, form.currency)} @ ${fxRate})` : ''}`,
          counterparty: form.beneficiary, beneficiary: form.beneficiary,
          accountNo: form.accountNo, bankName: form.receiverBank,
          receiverBIC: form.receiverBIC, receiverKTTCode: form.receiverKTTCode,
          currency: form.currency, keyTest: data.keyTest,
          valueDate: form.valueDate, uetr: data.uetr,
          rtgsSystem, priority,
          originalAmount: amt, originalCurrency: form.currency, fxRate,
          accountIban: sourceAccountIban || null,
          accountHolder: accounts?.find(a => a.iban === sourceAccountIban)?.holderName || null,
        });

        setSlip({
          ...form, txnRef: data.settlement.settlementId,
          method: `RTGS ${rtgsSystem} (${priority})`,
          keyTest: data.keyTest, kttEmail: KTT_EMAIL, kttCode: KTT_CODE,
          receiverKTTCode: form.receiverKTTCode, receiverBIC: form.receiverBIC,
        });
      }

      setStep(4);
      setAlert(data.success
        ? { type: 'success', msg: `RTGS settlement FINAL. UETR: ${data.uetr}` }
        : { type: 'error', msg: `Settlement failed: ${data.reason || 'Insufficient funds — queued for LSM'}` }
      );
    } catch (err) {
      setAlert({ type: 'error', msg: `Network error: ${err.message}` });
    }

    setSending(false);
  };

  // Reset
  const handleNew = () => {
    txnRef.current = generateTxnRef('RTGS');
    setForm({ beneficiary: '', accountNo: '', receiverBank: '', receiverBIC: '', receiverKTTCode: '', currency: 'USD', amount: '', narrative: '', valueDate: todayISO(), senderBIC: '' });
    setStep(1); setAlert(null); setResult(null); setBalanceChecks([]); setIsoMessage('');
  };

  // Review data
  const reviewRows = [
    { section: 'RTGS CONFIGURATION' },
    { label: 'RTGS System', value: RTGS_SYSTEMS.find(s => s.code === rtgsSystem)?.name || rtgsSystem },
    { label: 'Priority', value: `${priority} (Score: ${PRIORITIES.find(p => p.id === priority)?.score})`, highlight: true },
    { label: 'ISO 20022 Message', value: 'pacs.008.001.10 (FIToFICstmrCdtTrf)', mono: true },
    { section: 'SETTLEMENT DETAILS' },
    { label: 'Settlement Reference', value: txnRef.current, mono: true },
    { label: 'Key Test Number', value: keyTest, gold: true },
    { label: 'UETR', value: '(Generated on submission)', mono: true },
    { label: 'Beneficiary', value: form.beneficiary },
    { label: 'Account Number', value: form.accountNo },
    { label: 'Receiver Bank', value: form.receiverBank || '-' },
    { label: 'Receiver BIC', value: form.receiverBIC || '-' },
    { label: 'Receiver KTT Code', value: form.receiverKTTCode || '-' },
    { label: 'Sender BIC', value: form.senderBIC },
    { label: 'Sender KTT Code', value: KTT_CODE },
    { label: 'Currency', value: form.currency },
    { label: 'Amount', value: form.currency !== 'USD' ? formatFXAmount(parseFloat(form.amount || 0), form.currency) : formatUSD(parseFloat(form.amount || 0)), highlight: true },
    ...(form.currency !== 'USD' ? [
      { label: 'FX Rate', value: `1 USD = ${getFXRate(form.currency)} ${form.currency}`, mono: true },
      { label: 'USD Equivalent', value: formatUSD(toUSD(parseFloat(form.amount || 0), form.currency)), gold: true },
    ] : []),
    { label: 'Value Date', value: form.valueDate },
    { label: 'Narrative', value: form.narrative || '-' },
    { section: 'SETTLEMENT MECHANICS' },
    { label: 'Settlement Method', value: 'Real-Time Gross (Atomic Debit-Credit)' },
    { label: 'Finality', value: 'Irrevocable upon settlement' },
    { label: 'LSM', value: 'Auto (balance-reactive, bilateral offset)' },
    { label: 'Notification', value: 'camt.054 (Debit/Credit Notification)' },
  ];

  return (
    <div className="gap-16">
      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.type === 'success' ? '\u2713' : '\u2717'} {alert.msg}
        </div>
      )}

      {/* STEP INDICATOR */}
      <div style={{ display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {[
          { n: 1, label: 'RTGS System & Priority' },
          { n: 2, label: 'Payment Details' },
          { n: 3, label: 'Review & Confirm' },
          { n: 4, label: 'Settlement Result' },
        ].map(s => (
          <div key={s.n} style={{
            flex: 1, padding: '14px 16px', textAlign: 'center',
            background: step >= s.n ? (step === s.n ? 'var(--gold-soft)' : 'var(--surface2)') : 'var(--surface)',
            borderRight: s.n < 4 ? '1px solid var(--border)' : 'none',
            fontWeight: step === s.n ? 700 : 500, color: step >= s.n ? 'var(--gold)' : 'var(--text3)', fontSize: 12,
          }}>
            <span style={{ marginRight: 6, fontWeight: 700 }}>{s.n}</span>{s.label}
          </div>
        ))}
      </div>

      {/* ===== STEP 1: RTGS SYSTEM & PRIORITY ===== */}
      {step === 1 && (
        <div className="gap-16">
          {/* Architecture Info */}
          <div className="settle-bar">
            <div className="settle-dot" />
            <span className="settle-text">RTGS ENGINE — REAL-TIME GROSS SETTLEMENT</span>
            <span className="settle-time">Atomic | Irrevocable | Final</span>
          </div>

          <div className="grid2">
            <div className="card">
              <div className="card-header"><span className="card-title">Select RTGS System</span></div>
              <div className="card-body">
                <div className="gap-16">
                  {RTGS_SYSTEMS.map(sys => (
                    <div key={sys.code} onClick={() => setRtgsSystem(sys.code)} style={{
                      padding: '14px 18px',
                      border: rtgsSystem === sys.code ? '2px solid var(--gold)' : sys.custom ? '1px dashed var(--border)' : '1px solid var(--border)',
                      borderRadius: 'var(--radius)', cursor: 'pointer',
                      background: rtgsSystem === sys.code ? 'var(--gold-soft)' : sys.custom ? 'rgba(255,180,80,0.04)' : 'var(--surface)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{sys.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{sys.code}</div>
                        {sys.custom && <div style={{ fontSize: 10, color: 'var(--coral)', marginTop: 3, fontWeight: 600 }}>Licensed Financial Institutions Only — Pending Integration</div>}
                      </div>
                      {sys.custom
                        ? <span style={{ fontSize: 10, padding: '3px 8px', background: 'rgba(255,120,80,0.12)', color: 'var(--coral)', borderRadius: 6, fontWeight: 700, whiteSpace: 'nowrap' }}>AWAITING</span>
                        : <span className="badge badge-settled" style={{ fontSize: 10 }}>{sys.currency}</span>
                      }
                    </div>
                  ))}

                  {/* Custom RTGS registration panel */}
                  {rtgsSystem === 'CUSTOM-RTGS' && !customSubmitted && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface2)', overflow: 'hidden', marginTop: 8 }}>
                      <div style={{ padding: '12px 18px', background: 'rgba(255,120,80,0.08)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 16 }}>🏦</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>Custom RTGS Integration Request</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>Licensed Financial Institutions only — regulatory approval required before connection</div>
                        </div>
                      </div>
                      <div style={{ padding: '16px 18px' }}>
                        <div style={{ padding: '10px 14px', background: 'rgba(255,180,80,0.08)', border: '1px solid rgba(255,180,80,0.2)', borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                          ⚠️ <strong>Regulatory Requirement:</strong> Only central banks, licensed commercial banks, and regulated financial institutions may connect to Dwin RTGS infrastructure. Your institution must hold a valid banking license issued by a recognized financial regulator. Submission is subject to compliance review.
                        </div>
                        <div className="gap-16">
                          {[
                            { label: 'Institution Full Legal Name *', key: 'institutionName', placeholder: 'e.g. First National Bank of...' },
                            { label: 'BIC / SWIFT Code *', key: 'bic', placeholder: 'e.g. FNBKUS33XXX' },
                            { label: 'Banking License Number *', key: 'licenseNumber', placeholder: 'e.g. FRB-2024-00123' },
                          ].map(f => (
                            <div key={f.key} className="form-group">
                              <label className="form-label">{f.label}</label>
                              <input className="form-input" placeholder={f.placeholder} value={customForm[f.key]} onChange={setCustom(f.key)} />
                            </div>
                          ))}
                          <div className="grid2">
                            <div className="form-group">
                              <label className="form-label">License Type</label>
                              <select className="form-input" value={customForm.licenseType} onChange={setCustom('licenseType')}>
                                <option value="">Select...</option>
                                <option>Commercial Banking License</option>
                                <option>Central Bank</option>
                                <option>Payment Institution License</option>
                                <option>Electronic Money Institution</option>
                                <option>Foreign Bank Branch License</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">Country</label>
                              <input className="form-input" placeholder="e.g. United States" value={customForm.country} onChange={setCustom('country')} />
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Regulatory Authority (Issuing Regulator)</label>
                            <input className="form-input" placeholder="e.g. Federal Reserve, OCC, ECB, Bank of England..." value={customForm.regulator} onChange={setCustom('regulator')} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Preferred Settlement Currency</label>
                            <select className="form-input" value={customForm.currency} onChange={setCustom('currency')}>
                              <option>USD</option><option>EUR</option><option>GBP</option><option>JPY</option><option>CHF</option><option>AUD</option><option>CAD</option><option>THB</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">API / Connection Endpoint URL</label>
                            <input className="form-input" placeholder="https://rtgs.yourinstitution.com/api/v1" value={customForm.endpoint} onChange={setCustom('endpoint')} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Compliance Contact Email</label>
                            <input className="form-input" type="email" placeholder="compliance@yourinstitution.com" value={customForm.contactEmail} onChange={setCustom('contactEmail')} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Additional Notes</label>
                            <textarea className="form-input" rows={3} placeholder="Any additional information for the Dwin integration team..." value={customForm.notes} onChange={setCustom('notes')} />
                          </div>
                          <button className="btn btn-gold" style={{ width: '100%' }} onClick={handleCustomSubmit}>
                            Submit Integration Request
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {rtgsSystem === 'CUSTOM-RTGS' && customSubmitted && (
                    <div style={{ border: '1px solid rgba(80,200,120,0.3)', borderRadius: 'var(--radius)', background: 'rgba(80,200,120,0.06)', padding: '18px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Integration Request Submitted</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.7 }}>
                        Your request for <strong>{customForm.institutionName}</strong> ({customForm.bic}) has been received.<br/>
                        Dwin compliance team will review your credentials and contact you at <strong>{customForm.contactEmail || 'the email provided'}</strong>.<br/>
                        <span style={{ color: 'var(--gold)', fontWeight: 600 }}>Estimated review: 5–10 business days</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="gap-16">
              <div className="card">
                <div className="card-header"><span className="card-title">Settlement Priority (CHAPS Scale 0-89)</span></div>
                <div className="card-body">
                  <div className="gap-16">
                    {PRIORITIES.map(p => (
                      <div key={p.id} onClick={() => setPriority(p.id)} style={{
                        padding: '16px 18px', border: priority === p.id ? '2px solid var(--gold)' : '1px solid var(--border)',
                        borderRadius: 'var(--radius)', cursor: 'pointer',
                        background: priority === p.id ? 'var(--gold-soft)' : 'var(--surface)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, fontSize: 15 }}>{p.label}</span>
                          <span className="badge badge-ktt" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Score {p.score}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{p.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Architecture summary */}
              <div className="card">
                <div className="card-header"><span className="card-title">6-Layer Architecture</span></div>
                <div className="card-body" style={{ fontSize: 12 }}>
                  {[
                    ['L6', 'Participant Interface', 'var(--blue)'],
                    ['L5', 'Connectivity — ISO 20022 (pacs.008)', 'var(--teal)'],
                    ['L4', 'Gateway — TLS 1.3 / Mutual PKI', 'var(--gold)'],
                    ['L3', 'Processing — Queue, Priority, LSM, Gridlock', 'var(--coral)'],
                    ['L2', 'Settlement Engine — Atomic Debit-Credit', 'var(--navy)'],
                    ['L1', 'Account Layer — ESA, Collateral, Omnibus', 'var(--text2)'],
                  ].map(([layer, desc, color]) => (
                    <div key={layer} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
                      <span style={{ fontWeight: 700, color, minWidth: 28, fontFamily: "'IBM Plex Mono', monospace" }}>{layer}</span>
                      <span style={{ color: 'var(--text2)' }}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button className="btn btn-gold btn-lg" style={{ width: '100%' }} onClick={handleConfigDone}>
                Continue to Payment Details &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 2: PAYMENT DETAILS ===== */}
      {step === 2 && (
        <div className="grid2">
          <div className="gap-16">
            {/* Config summary */}
            <div className="card" style={{ background: 'var(--surface2)' }}>
              <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>RTGS: </span>
                  <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{rtgsSystem}</span>
                  <span style={{ margin: '0 8px', color: 'var(--border)' }}>|</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>PRIORITY: </span>
                  <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{priority}</span>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setStep(1)}>Edit</button>
              </div>
            </div>

            {/* Key Test */}
            <div className="kt-box">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div className="kt-label">KEY TEST</div>
                  <div className="kt-value">{keyTest}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="kt-label">REFERENCE</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, color: 'var(--gold)', fontWeight: 600, marginTop: 6 }}>{txnRef.current}</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">RTGS Payment Details</span></div>
              <div className="card-body">
                <div className="gap-16">
                  {[
                    { label: 'Beneficiary *', key: 'beneficiary', placeholder: 'Enter beneficiary full legal name' },
                    { label: 'Account / IBAN *', key: 'accountNo', placeholder: 'Enter account number or IBAN' },
                    { label: 'Receiver Bank / FI', key: 'receiverBank', placeholder: 'Enter receiver bank name' },
                    { label: 'Receiver BIC/SWIFT', key: 'receiverBIC', placeholder: 'Enter BIC/SWIFT code' },
                    { label: 'Receiver KTT Code', key: 'receiverKTTCode', placeholder: 'Enter receiver KTT code' },
                    { label: 'Sender BIC', key: 'senderBIC', placeholder: 'Enter sender BIC' },
                  ].map(f => (
                    <div key={f.key} className="form-group">
                      <label className="form-label">{f.label}</label>
                      <input className="form-input" placeholder={f.placeholder} value={form[f.key]} onChange={set(f.key)} />
                    </div>
                  ))}
                  <div className="grid2">
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <select className="form-input" value={form.currency} onChange={set('currency')}>
                        <option>USD</option><option>EUR</option><option>GBP</option><option>JPY</option><option>CHF</option><option>AUD</option><option>CAD</option><option>THB</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Amount *</label>
                      <input className="form-input" type="number" placeholder="0.00" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Value Date</label>
                    <input className="form-input" type="date" value={form.valueDate} onChange={set('valueDate')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Remittance Info / Narrative</label>
                    <textarea className="form-input" value={form.narrative} onChange={set('narrative')} placeholder="Payment reference..." />
                  </div>
                  {accounts && accounts.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">Source Account</label>
                      <select className="form-input" value={sourceAccountIban} onChange={e => setSourceAccountIban(e.target.value)}>
                        <option value="">Main Wallet</option>
                        {accounts.filter(a => a.status === 'ACTIVE').map(a => (
                          <option key={a.iban} value={a.iban}>{a.ibanFormatted} — {a.holderName} — {formatUSD(a.balance)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <span style={{ fontSize: 14, color: 'var(--text2)' }}>
                      Available: <strong style={{ color: 'var(--teal)', fontSize: 16 }}>{formatUSD(sourceAccountIban ? (accounts?.find(a => a.iban === sourceAccountIban)?.balance || 0) : wallet.balance)}</strong>
                    </span>
                    <button className="btn btn-gold btn-lg" onClick={handleGoToReview}>Review Before Settlement &rarr;</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 8-Step Lifecycle */}
          <div className="card" style={{ alignSelf: 'start', position: 'sticky', top: 0 }}>
            <div className="card-header"><span className="card-title">Settlement Lifecycle (8 Steps)</span></div>
            <div className="card-body">
              {[
                { n: 1, label: 'Submission & Validation', status: 'current' },
                { n: 2, label: 'Queue Insertion (Priority-Ordered)' },
                { n: 3, label: 'Balance Check (5 Sequential Checks)' },
                { n: 4, label: 'LSM Check (4 Types)' },
                { n: 5, label: 'Atomic Debit-Credit' },
                { n: 6, label: 'Finality Declaration' },
                { n: 7, label: 'Notification (camt.054)' },
                { n: 8, label: 'Reconciliation' },
              ].map(s => (
                <div key={s.n} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-light)', alignItems: 'center' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: s.status === 'current' ? 'var(--gold)' : 'var(--surface2)',
                    color: s.status === 'current' ? 'white' : 'var(--text3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>{s.n}</div>
                  <span style={{ fontSize: 13, color: s.status === 'current' ? 'var(--navy)' : 'var(--text2)', fontWeight: s.status === 'current' ? 600 : 400 }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 3: REVIEW ===== */}
      {step === 3 && (
        <div className="card" style={{ maxWidth: 740, margin: '0 auto' }}>
          <div className="card-header">
            <span className="card-title">RTGS Settlement — Review & Confirm</span>
            <span className="badge badge-pending" style={{ fontSize: 11, padding: '4px 12px' }}>PENDING SUBMISSION</span>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.7 }}>
              Review all details carefully. Once confirmed, the settlement will be processed through the RTGS engine.
              Settlement is <strong style={{ color: 'var(--navy)' }}>atomic, irrevocable, and final</strong>.
            </div>

            {reviewRows.map((r, i) => {
              if (r.section) {
                return (
                  <div key={i} style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.2em', padding: '16px 0 8px', borderBottom: '2px solid var(--border)', marginTop: i > 0 ? 12 : 0 }}>
                    {r.section}
                  </div>
                );
              }
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600, minWidth: 200 }}>{r.label}</span>
                  <span style={{
                    fontSize: r.highlight ? 16 : 13, fontWeight: r.highlight || r.gold ? 700 : 500,
                    color: r.gold ? 'var(--gold)' : r.highlight ? 'var(--navy)' : 'var(--text)',
                    fontFamily: r.mono || r.gold ? "'IBM Plex Mono', monospace" : 'inherit',
                    letterSpacing: r.gold ? '0.2em' : 'normal',
                    textAlign: 'right', maxWidth: '55%', wordBreak: 'break-all',
                  }}>{r.value}</span>
                </div>
              );
            })}

            <div style={{ marginTop: 28, display: 'flex', gap: 12, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" onClick={() => setStep(1)}>Edit Config</button>
                <button className="btn btn-outline" onClick={() => setStep(2)}>&larr; Edit Payment</button>
              </div>
              <button className="btn btn-gold btn-lg" onClick={handleSubmitSettlement} disabled={sending}>
                {sending ? 'Processing Settlement...' : 'Submit to RTGS Engine'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 4: SETTLEMENT RESULT ===== */}
      {step === 4 && result && (
        <div className="gap-16">
          {/* Result Header */}
          <div className="card" style={{ textAlign: 'center' }}>
            <div className="card-body" style={{ padding: 40 }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: result.success ? 'linear-gradient(135deg, var(--teal), var(--teal-light))' : 'linear-gradient(135deg, var(--coral), #FF9999)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px', fontSize: 32, color: 'white',
              }}>{result.success ? '\u2713' : '\u2717'}</div>
              <h3 style={{ fontFamily: "'Source Serif 4', serif", fontSize: 24, color: 'var(--navy)', marginBottom: 8 }}>
                {result.success ? 'Settlement FINAL — Irrevocable' : 'Settlement Queued / Pending'}
              </h3>
              {result.settlement && (
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                  UETR: <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: 'var(--gold)' }}>{result.settlement.uetr}</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid2">
            {/* Balance Checks */}
            <div className="card">
              <div className="card-header"><span className="card-title">5-Step Balance Verification</span></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table>
                  <thead><tr><th>Step</th><th>Check</th><th>Value</th><th style={{ textAlign: 'center' }}>Result</th></tr></thead>
                  <tbody>
                    {(balanceChecks || []).map((c, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 700 }}>{c.step}</td>
                        <td>{c.name}</td>
                        <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
                          {typeof c.value === 'number' ? formatUSD(c.value) : String(c.value)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex', width: 24, height: 24, borderRadius: '50%',
                            alignItems: 'center', justifyContent: 'center', fontSize: 14,
                            background: c.pass ? 'rgba(13,148,136,0.1)' : 'rgba(231,76,111,0.1)',
                            color: c.pass ? 'var(--teal)' : 'var(--coral)',
                          }}>{c.pass ? '\u2713' : '\u2717'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Settlement Details */}
            {result.settlement && (
              <div className="card">
                <div className="card-header"><span className="card-title">Settlement Record</span></div>
                <div className="card-body">
                  {[
                    ['Settlement ID', result.settlement.settlementId],
                    ['Status', result.settlement.status],
                    ['UETR', result.settlement.uetr],
                    ['Key Test', result.settlement.keyTest],
                    ['Priority', `${result.settlement.priority} (${result.settlement.priorityScore})`],
                    ['LSM Applied', result.settlement.lsmApplied],
                    ['Amount', formatUSD(result.settlement.amount)],
                    ['Settled At', result.settlement.settledAt || 'Queued'],
                    ['Sender Balance', result.senderBalance !== undefined ? formatUSD(result.senderBalance) : '-'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)' }}>{k}</span>
                      <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ISO 20022 pacs.008 XML */}
          {isoMessage && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">ISO 20022 pacs.008 — Settlement Message</span>
                <button className="btn btn-outline btn-sm" onClick={() => navigator.clipboard.writeText(isoMessage)}>Copy XML</button>
              </div>
              <div className="card-body">
                <div className="code-block" style={{ maxHeight: 400 }}>{isoMessage}</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button className="btn btn-gold btn-lg" onClick={handleNew}>New RTGS Settlement</button>
            <button className="btn btn-outline btn-lg" onClick={() => window.print()}>Print Receipt</button>
          </div>
        </div>
      )}
    </div>
  );
}
